import { FC, memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiChevronDown, HiChevronUp, HiOutlineChevronUpDown } from 'react-icons/hi2';
import { KidStatus, KidTemplateAffectedShareClass } from '../../../../../api/kids/types';
import { useAllShareClasses } from '../../../../../api/shareclass';
import { cn } from '../../../../../utilities/cn';
import SearchBar from '../../../../ui/inputs/SearchBar';
import {
  EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
  EMPTY_KID_TEMPLATE_DOCUMENT_ROWS,
  enrichKidTemplateDocumentRowsWithShareClasses,
  formatKidTemplateFundId,
  formatKidTemplateFundName,
  formatKidTemplateShareClassName,
  KidTemplateDocumentCoverageState,
  KidTemplateDocumentRow,
  kidTemplateDocumentCoverageBadgeClass,
  kidTemplateDocumentCoverageChipLabel,
  kidTemplateDocumentCoverageLabel,
  kidTemplateDocumentCoverageSortRank,
  kidTemplateDocumentRowKey,
  mergeKidTemplateDocumentRows,
} from '../templateDocumentRows.utils';
import { AddTemplateToDocumentContext } from '../templateManagement.types';
import { buildKidEditorV2Url } from '../templateManagement.utils';
import AddTemplateToDocumentModal from './AddTemplateToDocumentModal';
import { sortFundNames } from '../../../../../utilities/customSorting';

type SortColumn =
  | 'shareClassName'
  | 'isin'
  | 'fundId'
  | 'fundName'
  | 'status'
  | 'lastUpdated';

type SortState = {
  column: SortColumn;
  direction: 'asc' | 'desc';
};

const SORTABLE_COLUMNS: { id: SortColumn; label: string }[] = [
  { id: 'fundId', label: 'Fund ID' },
  { id: 'fundName', label: 'Fund Name' },
  { id: 'shareClassName', label: 'Share Class Name' },
  { id: 'isin', label: 'ISIN' },
  { id: 'status', label: 'Status' },
  { id: 'lastUpdated', label: 'Last updated' },
];

const DEFAULT_SORT: SortState = { column: 'fundId', direction: 'asc' };

const formatLastUpdated = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const documentMatchesQuery = (
  document: KidTemplateDocumentRow,
  query: string,
): boolean => {
  const haystack = [
    formatKidTemplateShareClassName(document),
    document.share_class_isin,
    document.share_class_id,
    document.fund_name,
    document.fund_name_full,
    document.fund_name_long,
    document.fund_id,
    kidTemplateDocumentCoverageChipLabel[document.coverageState],
    document.last_updated_by_name,
    document.last_updated_by,
    formatLastUpdated(document.last_updating_datetime),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
};

const compareDocuments = (
  a: KidTemplateDocumentRow,
  b: KidTemplateDocumentRow,
  column: SortColumn,
): number => {
  switch (column) {
    case 'shareClassName':
      return formatKidTemplateShareClassName(a).localeCompare(
        formatKidTemplateShareClassName(b),
        undefined,
        { sensitivity: 'base' },
      );
    case 'isin':
      return (a.share_class_isin || '').localeCompare(b.share_class_isin || '', undefined, {
        sensitivity: 'base',
      });
    case 'fundId': {
      const fundCompare = sortFundNames(
        formatKidTemplateFundId(a),
        formatKidTemplateFundId(b),
      );
      if (fundCompare !== 0) return fundCompare;
      return formatKidTemplateShareClassName(a).localeCompare(
        formatKidTemplateShareClassName(b),
        undefined,
        { sensitivity: 'base' },
      );
    }
    case 'fundName':
      return formatKidTemplateFundName(a).localeCompare(
        formatKidTemplateFundName(b),
        undefined,
        { sensitivity: 'base' },
      );
    case 'status':
      return (
        kidTemplateDocumentCoverageSortRank[a.coverageState] -
        kidTemplateDocumentCoverageSortRank[b.coverageState]
      );
    case 'lastUpdated': {
      const aTime = a.last_updating_datetime
        ? Date.parse(a.last_updating_datetime)
        : 0;
      const bTime = b.last_updating_datetime
        ? Date.parse(b.last_updating_datetime)
        : 0;
      return aTime - bTime;
    }
    default:
      return 0;
  }
};

const DocumentStatusBadge: FC<{
  coverageState: KidTemplateDocumentRow['coverageState'];
}> = ({ coverageState }) => (
  <span
    className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${kidTemplateDocumentCoverageBadgeClass[coverageState]}`}
  >
    {kidTemplateDocumentCoverageChipLabel[coverageState]}
  </span>
);

const SortIndicator: FC<{
  sorted: false | 'asc' | 'desc';
  isPage: boolean;
}> = ({ sorted, isPage }) => {
  if (sorted === 'asc') {
    return (
      <HiChevronUp
        className={cn('h-3.5 w-3.5 shrink-0', isPage ? 'text-white' : 'text-gray-600')}
        aria-hidden
      />
    );
  }
  if (sorted === 'desc') {
    return (
      <HiChevronDown
        className={cn('h-3.5 w-3.5 shrink-0', isPage ? 'text-white' : 'text-gray-600')}
        aria-hidden
      />
    );
  }
  return (
    <HiOutlineChevronUpDown
      className={cn(
        'h-3.5 w-3.5 shrink-0',
        isPage ? 'text-blue-200/70' : 'text-gray-400',
      )}
      aria-hidden
    />
  );
};

const AffectedShareClassesList: FC<{
  clientId: string;
  rows?: KidTemplateAffectedShareClass[];
  documentRows?: KidTemplateDocumentRow[];
  missingRows?: KidTemplateAffectedShareClass[];
  invalidRows?: KidTemplateAffectedShareClass[];
  addTemplateContext?: AddTemplateToDocumentContext;
  emptyLabel: string;
  className?: string;
  /** `embedded` = compact nested table; `page` = Fund Overview-style flat table */
  variant?: 'embedded' | 'page';
  /** When set, the table is filtered by this value and no search bar is rendered. */
  searchQuery?: string;
  /** Coverage status filter. `all` (or omitted) shows every document. */
  statusFilter?: 'all' | KidTemplateDocumentCoverageState;
  onPreviewDocument?: (document: KidTemplateDocumentRow) => void;
  onRemoveDocument?: (document: KidTemplateDocumentRow) => void;
}> = ({
  clientId,
  rows,
  documentRows,
  missingRows = EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
  invalidRows = EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
  addTemplateContext,
  emptyLabel,
  className = '',
  variant = 'embedded',
  searchQuery,
  statusFilter = 'all',
  onPreviewDocument,
  onRemoveDocument,
}) => {
  const [addTarget, setAddTarget] = useState<KidTemplateDocumentRow | null>(
    null,
  );
  const [internalSearch, setInternalSearch] = useState('');
  const [sorting, setSorting] = useState<SortState>(DEFAULT_SORT);
  const isPage = variant === 'page';
  const isSearchControlled = searchQuery != null;
  const globalFilter = isSearchControlled ? searchQuery : internalSearch;
  const { data: allShareClasses } = useAllShareClasses({ client_id: clientId });

  const mergedRows = useMemo(() => {
    if (documentRows) return documentRows;

    if (rows || missingRows.length > 0 || invalidRows.length > 0) {
      return enrichKidTemplateDocumentRowsWithShareClasses(
        mergeKidTemplateDocumentRows(
          rows ?? EMPTY_KID_TEMPLATE_AFFECTED_ROWS,
          missingRows,
          invalidRows,
        ),
        allShareClasses,
      );
    }

    return EMPTY_KID_TEMPLATE_DOCUMENT_ROWS;
  }, [documentRows, rows, missingRows, invalidRows, allShareClasses]);

  const visibleRows = useMemo(() => {
    const query = globalFilter.trim().toLowerCase();
    const filtered =
      statusFilter === 'all' && !query
        ? mergedRows
        : mergedRows.filter((row) => {
            if (
              statusFilter !== 'all' &&
              row.coverageState !== statusFilter
            ) {
              return false;
            }
            return !query || documentMatchesQuery(row, query);
          });

    const sorted = [...filtered].sort((a, b) => {
      const result = compareDocuments(a, b, sorting.column);
      return sorting.direction === 'desc' ? -result : result;
    });
    return sorted;
  }, [globalFilter, mergedRows, sorting, statusFilter]);

  const toggleSort = (column: SortColumn) => {
    setSorting((current) => {
      if (current?.column !== column) {
        return { column, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return DEFAULT_SORT;
    });
  };

  if (mergedRows.length === 0) {
    return <p className="text-sm text-gray-500">{emptyLabel}</p>;
  }

  const isFiltered =
    globalFilter.trim().length > 0 || statusFilter !== 'all';

  return (
    <>
      <div>
        {isSearchControlled ? null : (
          <div className={cn('mb-2 flex justify-end', !isPage && 'mt-2')}>
            <SearchBar
              searchHandler={setInternalSearch}
              placeholder="Search documents…"
              className={cn('w-64 shrink-0', !isPage && 'py-1 text-xs')}
            />
          </div>
        )}
        <div
          className={cn(
            isPage
              ? 'overflow-x-auto'
              : 'max-h-52 overflow-auto rounded-md border border-gray-200',
            className,
          )}
        >
          <table
            className={cn('w-full border-collapse', !isPage && 'min-w-full text-xs')}
          >
            <thead
              className={cn(
                isPage
                  ? 'bg-mainblue text-white'
                  : 'sticky top-0 z-[1] bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500',
              )}
            >
              <tr>
                {SORTABLE_COLUMNS.map((column) => {
                  const sorted =
                    sorting?.column === column.id ? sorting.direction : false;

                  return (
                    <th
                      key={column.id}
                      className={cn(
                        isPage
                          ? 'px-4 py-3 text-left text-sm font-medium whitespace-nowrap'
                          : 'border-b border-gray-200 px-2 py-1 font-bold',
                        isPage
                          ? 'cursor-pointer select-none hover:bg-white/10'
                          : 'cursor-pointer select-none hover:bg-gray-100',
                      )}
                      onClick={() => toggleSort(column.id)}
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : 'none'
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {column.label}
                        <SortIndicator sorted={sorted} isPage={isPage} />
                      </span>
                    </th>
                  );
                })}
                <th
                  className={cn(
                    isPage
                      ? 'px-4 py-3 text-left text-sm font-medium whitespace-nowrap'
                      : 'border-b border-gray-200 px-2 py-1 text-right font-bold',
                  )}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={cn(!isPage && 'divide-y divide-gray-100 bg-white')}>
              {visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={SORTABLE_COLUMNS.length + 1}
                    className={cn(
                      'text-center text-gray-500',
                      isPage ? 'px-4 py-8 text-sm' : 'px-2 py-4 text-xs',
                    )}
                  >
                    {isFiltered
                      ? 'No documents match your search or filter.'
                      : emptyLabel}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => {
                  const canAdd =
                    addTemplateContext && row.coverageState === 'missing';
                  const canRemove =
                    onRemoveDocument &&
                    addTemplateContext &&
                    row.coverageState === 'invalid';
                  const canPreview =
                    onPreviewDocument && row.coverageState === 'present';
                  const isDocumentLocked = row.status === KidStatus.EDITED;
                  const updatedBy =
                    row.last_updated_by_name || row.last_updated_by || 'Unknown';
                  const lastUpdatedAt = formatLastUpdated(
                    row.last_updating_datetime,
                  );

                  return (
                    <tr
                      key={kidTemplateDocumentRowKey(row)}
                      className={cn(
                        isPage
                          ? 'border-b border-gray-200 hover:bg-gray-50'
                          : 'hover:bg-gray-50/80',
                      )}
                    >
                      <td
                        className={cn(
                          isPage
                            ? 'max-w-[10rem] px-4 py-3 text-sm text-gray-700'
                            : 'max-w-[7rem] px-2 py-1.5 align-middle text-gray-600',
                        )}
                      >
                        <span
                          className="block truncate"
                          title={formatKidTemplateFundId(row)}
                        >
                          {formatKidTemplateFundId(row)}
                        </span>
                      </td>
                      <td
                        className={cn(
                          isPage
                            ? 'max-w-[14rem] px-4 py-3 text-sm text-gray-700'
                            : 'max-w-[9rem] px-2 py-1.5 align-middle text-gray-600',
                        )}
                      >
                        <span
                          className="block truncate"
                          title={formatKidTemplateFundName(row)}
                        >
                          {formatKidTemplateFundName(row)}
                        </span>
                      </td>
                      <td
                        className={cn(
                          isPage
                            ? 'max-w-[12rem] px-4 py-3 text-sm'
                            : 'max-w-[8rem] px-2 py-1.5 align-middle text-xs',
                        )}
                      >
                        <span
                          className="block truncate font-medium text-gray-900"
                          title={formatKidTemplateShareClassName(row)}
                        >
                          {formatKidTemplateShareClassName(row)}
                        </span>
                      </td>
                      <td
                        className={cn(
                          isPage
                            ? 'max-w-[9rem] px-4 py-3 font-mono text-sm text-gray-600'
                            : 'max-w-[7rem] px-2 py-1.5 align-middle font-mono text-[11px] text-gray-600',
                        )}
                      >
                        <span
                          className="block truncate"
                          title={row.share_class_isin || undefined}
                        >
                          {row.share_class_isin || '—'}
                        </span>
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap',
                          isPage ? 'px-4 py-3 text-sm' : 'px-2 py-1.5 align-middle',
                        )}
                      >
                        <DocumentStatusBadge coverageState={row.coverageState} />
                      </td>
                      <td
                        className={cn(
                          isPage
                            ? 'max-w-[10rem] px-4 py-3 text-sm'
                            : 'max-w-[9rem] px-2 py-1.5 align-middle',
                        )}
                      >
                        <span
                          className={cn(
                            'block truncate text-gray-700',
                            !isPage && 'text-[11px]',
                          )}
                          title={lastUpdatedAt}
                        >
                          {lastUpdatedAt}
                        </span>
                        <span
                          className={cn(
                            'block truncate text-gray-500',
                            isPage ? 'text-xs' : 'text-[10px]',
                          )}
                          title={updatedBy}
                        >
                          {updatedBy}
                        </span>
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap',
                          isPage
                            ? 'px-4 py-3 text-sm'
                            : 'px-2 py-1.5 text-right align-middle',
                        )}
                      >
                        <div
                          className={cn(
                            'inline-flex items-center gap-1.5',
                            !isPage && 'justify-end',
                          )}
                        >
                          <Link
                            to={buildKidEditorV2Url(row)}
                            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            title="Open in editor"
                          >
                            Go to Editor
                          </Link>
                          {canPreview ? (
                            <button
                              type="button"
                              className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                              onClick={() => onPreviewDocument(row)}
                              title="Preview template in this document"
                            >
                              Preview
                            </button>
                          ) : null}
                          {canAdd ? (
                            <button
                              type="button"
                              className="inline-flex items-center rounded bg-mainblue px-2.5 py-1 text-xs font-semibold text-white hover:bg-mainblue_hover disabled:cursor-not-allowed disabled:opacity-45"
                              onClick={() => setAddTarget(row)}
                              title={
                                kidTemplateDocumentCoverageLabel[row.coverageState]
                              }
                            >
                              Add to Document
                            </button>
                          ) : null}
                          {canRemove ? (
                            <button
                              type="button"
                              className="inline-flex items-center rounded border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-900 hover:bg-red-100"
                              onClick={() => onRemoveDocument?.(row)}
                              title={
                                isDocumentLocked
                                  ? 'Mark the document as draft in the editor before changing the layout.'
                                  : kidTemplateDocumentCoverageLabel[
                                      row.coverageState
                                    ]
                              }
                            >
                              Remove from document
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addTarget && addTemplateContext ? (
        <AddTemplateToDocumentModal
          isOpen
          onClose={() => setAddTarget(null)}
          document={addTarget}
          {...addTemplateContext}
        />
      ) : null}
    </>
  );
};

export default memo(AffectedShareClassesList);
